import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  addDoc,
  updateDoc,
  getDocs,
  serverTimestamp,
} from '@angular/fire/firestore';
import type {
  OnboardingChildProfileSection,
  OnboardingFirstMemoryData,
  CircleInvitation,
  UserOnboardingState,
  ChildDocument,
} from '../models/onboarding.model';

/**
 * Data access layer for onboarding. Only used by OnboardingService.
 * Centralizes all Firestore reads/writes for the onboarding flow.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingDataAccessService {
  private readonly firestore = inject(Firestore);

  /** Load user onboarding state and children from Firestore */
  async loadUserOnboarding(uid: string): Promise<{
    state: UserOnboardingState | null;
    children: ChildDocument[];
  }> {
    const userDocRef = doc(this.firestore, `users/${uid}`);
    const userSnapshot = await getDoc(userDocRef);

    if (!userSnapshot.exists()) {
      return { state: null, children: [] };
    }

    const data = userSnapshot.data();
    const onboarding = data['onboarding'] as UserOnboardingState | undefined;
    const legacyOnboarding = data['onboardingData'] as
      | { children?: Array<{ id?: string; name: string; birthDate: string; gender: string }> }
      | undefined;

    let children: ChildDocument[] = [];
    const childrenRef = collection(this.firestore, `users/${uid}/children`);
    const childrenSnapshot = await getDocs(childrenRef);

    if (!childrenSnapshot.empty) {
      children = childrenSnapshot.docs.map((d) => {
        const docData = d.data();
        return {
          id: d.id,
          name: docData['name'] ?? '',
          birthDate: docData['birthDate'] ?? '',
          gender: (docData['gender'] ?? docData['relationship'] ?? 'boy') as 'boy' | 'girl',
          photoURL: docData['photoURL'],
          photoStoragePath: docData['photoStoragePath'],
          firstMemory: docData['firstMemory'],
          createdAt: docData['createdAt'] ?? '',
        };
      });
    } else if (legacyOnboarding?.children?.length) {
      // Migration: legacy onboardingData.children existed but no children subcollection
      children = legacyOnboarding.children.map((c, i) => ({
        id: c.id ?? `legacy-${i}`,
        name: c.name,
        birthDate: c.birthDate,
        gender: (c.gender ?? 'boy') as 'boy' | 'girl',
        createdAt: new Date().toISOString(),
      }));
    }

    const state: UserOnboardingState | null = onboarding
      ? {
          ...onboarding,
          childrenIds: onboarding.childrenIds ?? children.map((c) => c.id),
        }
      : null;

    return { state, children };
  }

  /** Create a child document and return its id */
  async addChild(
    uid: string,
    profile: OnboardingChildProfileSection
  ): Promise<string> {
    const childrenRef = collection(this.firestore, `users/${uid}/children`);
    const data: Record<string, unknown> = {
      name: profile.name,
      birthDate: profile.birthDate,
      gender: profile.gender,
      createdAt: new Date().toISOString(),
    };
    if (profile.photoURL) data['photoURL'] = profile.photoURL;
    if (profile.photoStoragePath) data['photoStoragePath'] = profile.photoStoragePath;
    const docRef = await addDoc(childrenRef, data);
    return docRef.id;
  }

  /** Update user onboarding state using dot-notation to avoid extra getDoc read */
  async updateUserOnboarding(
    uid: string,
    state: Partial<UserOnboardingState>
  ): Promise<void> {
    const userDocRef = doc(this.firestore, `users/${uid}`);
    // Build dot-notation update map to merge only the provided fields
    const update: Record<string, unknown> = { 'onboarding.updatedAt': serverTimestamp() };
    for (const [key, value] of Object.entries(state)) {
      if (key !== 'updatedAt') {
        update[`onboarding.${key}`] = value;
      }
    }
    await updateDoc(userDocRef, update);
  }

  /** Add/update first memory for a child */
  async addFirstMemoryToChild(
    uid: string,
    childId: string,
    memory: OnboardingFirstMemoryData
  ): Promise<void> {
    const childDocRef = doc(
      this.firestore,
      `users/${uid}/children/${childId}`
    );
    await updateDoc(childDocRef, {
      firstMemory: memory,
      updatedAt: serverTimestamp(),
    });
  }

  /** Save circle invitations to user doc */
  async saveInviteCircle(
    uid: string,
    invitations: CircleInvitation[]
  ): Promise<void> {
    const userDocRef = doc(this.firestore, `users/${uid}`);
    await updateDoc(userDocRef, {
      'onboarding.inviteCircle': { invitations },
      'onboarding.updatedAt': serverTimestamp(),
    });
  }

  /**
   * Create or update a memory doc at the canonical path:
   * users/{uid}/children/{childId}/memories/{memoryId}
   *
   * Includes memoryMonthDay ("MM-DD") for efficient On This Day queries.
   */
  async createMemoryDoc(
    uid: string,
    childId: string,
    memory: OnboardingFirstMemoryData
  ): Promise<string> {
    const memoriesRef = collection(
      this.firestore,
      `users/${uid}/children/${childId}/memories`
    );

    // Derive MM-DD string from memoryDate for indexed On This Day queries (A-1)
    const memoryMonthDay = memory.memoryDate
      ? memory.memoryDate.slice(5, 10) // "YYYY-MM-DD" → "MM-DD"
      : null;

    const hasPhoto = !!memory.photoUrl;
    const hasAudio = !!memory.audioUrl;
    const memoryType = hasPhoto && hasAudio ? 'mixed' : hasPhoto ? 'photo' : hasAudio ? 'audio' : 'text';
    const photoUrls = hasPhoto ? [memory.photoUrl] : [];

    if (memory.id) {
      const docRef = doc(
        this.firestore,
        `users/${uid}/children/${childId}/memories/${memory.id}`
      );
      await updateDoc(docRef, {
        title: memory.title || null,
        content: memory.content,
        memoryDate: memory.memoryDate ?? null,
        memoryMonthDay,
        photoUrl: memory.photoUrl ?? null,
        photoUrls,
        audioUrl: memory.audioUrl ?? null,
        type: memoryType,
        updatedAt: serverTimestamp(),
      });
      return memory.id;
    } else {
      const docRef = await addDoc(memoriesRef, {
        childId,
        title: memory.title || null,
        content: memory.content,
        memoryDate: memory.memoryDate ?? null,
        memoryMonthDay,
        photoUrl: memory.photoUrl ?? null,
        photoUrls,
        audioUrl: memory.audioUrl ?? null,
        type: memoryType,
        fromOnboarding: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    }
  }

  /** Remove a child document */
  async removeChild(uid: string, childId: string): Promise<void> {
    const childDocRef = doc(
      this.firestore,
      `users/${uid}/children/${childId}`
    );
    await deleteDoc(childDocRef);
  }

  /** Remove a memory document (used for rollback when addFirstMemoryToChild fails) */
  async removeMemory(uid: string, childId: string, memoryId: string): Promise<void> {
    const memoryDocRef = doc(
      this.firestore,
      `users/${uid}/children/${childId}/memories/${memoryId}`
    );
    await deleteDoc(memoryDocRef);
  }

  /**
   * Reads users/{uid} and resolves the current onboarding navigation target.
   * Returns one of:
   *  - `{ redirect: 'home' }` — onboarding already completed
   *  - `{ redirect: 'welcome' | string }` — step to navigate to
   *  - `{ redirect: null }` — user document does not exist (send to welcome)
   *
   * Shared by onboarding.guard.ts and onboarding-entry.guard.ts to avoid
   * duplicating Firestore read + parse logic.
   */
  async resolveOnboardingStep(uid: string): Promise<{
    completed: boolean;
    step: string;
    docExists: boolean;
  }> {
    const userDocRef = doc(this.firestore, `users/${uid}`);
    const snapshot = await getDoc(userDocRef);

    if (!snapshot.exists()) {
      return { completed: false, step: 'welcome', docExists: false };
    }

    const data = snapshot.data();
    const onboarding = data['onboarding'] as
      | { completedAt?: string | null; currentStep?: string }
      | undefined;

    if (!onboarding) {
      return { completed: false, step: 'welcome', docExists: true };
    }

    if (onboarding.completedAt) {
      return { completed: true, step: 'home', docExists: true };
    }

    return {
      completed: false,
      step: onboarding.currentStep ?? 'welcome',
      docExists: true,
    };
  }

  /** Ensure user doc exists (for init) */
  async ensureUserDoc(
    uid: string,
    opts: { email?: string; displayName?: string; photoURL?: string | null; preferredLanguage?: string }
  ): Promise<void> {
    const userDocRef = doc(this.firestore, `users/${uid}`);
    await setDoc(
      userDocRef,
      {
        uid,
        email: opts.email ?? '',
        displayName: opts.displayName ?? '',
        photoURL: opts.photoURL ?? null,
        preferredLanguage: opts.preferredLanguage ?? 'es',
        accountType: 'parent',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        onboarding: {
          currentStep: 'welcome',
          completedSteps: [],
          childrenIds: [],
          updatedAt: serverTimestamp(),
        },
      },
      { merge: true }
    );
  }
}
